import React, { useCallback, useMemo, useState } from 'react';
import Image from 'next/image';
import NextLink from 'next/link';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/ssr';
import { useAppData } from '../../hooks/useAppData';
import { useField } from '../../hooks/useField';
import { useOrganizationData } from '@/contexts/OrganizationContext';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTimezoneFormatter } from '@/hooks/useTimezoneFormatter';
import {
  Alert,
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  IconButton,
  Toolbar,
  Tooltip,
  Popover,
  Typography,
  Divider,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { alpha, styled } from '@mui/system';
import { MenuIcon } from '../icons/MenuIcon';
import { LanguagePopover } from '../../components/main/language-popover';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { DarkMode } from '@mui/icons-material';
import { CogIcon } from '../icons/CogIcon';
import type { Organization } from '../../types/organization';
import type { User } from '../../types/user';
import toast from 'react-hot-toast';
import { utils } from '../../utils';
import { getOrganizationStatusMessage } from '../../utils/organizationStatus';
import { pushIntercomEvent } from '../../utils/intercomDataLayer';
import { runSessionLogout } from '@/session/logout';
import { useCommittedOrganization } from '@/session/OrganizationIdentityContext';

const languages = {
  en: '/static/icons/us_flag.svg',
  de: '/static/icons/de_flag.svg',
  es: '/static/icons/es_flag.svg',
} as const;

type LanguageKey = keyof typeof languages;

const MainNavbarRoot = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  ...(theme.palette.mode === 'light'
    ? {
        boxShadow: (theme.shadows as string[])[3] || 'none',
      }
    : {
        backgroundColor: theme.palette.background.paper,
        borderBottomColor: theme.palette.divider,
        borderBottomStyle: 'solid',
        borderBottomWidth: 1,
        boxShadow: 'none',
      }),
}));

export interface MainNavbarProps {
  onOpenSidebar?: () => void;
  user?: User | null;
}

const TimezoneDisplay: React.FC = () => {
  const { timezone, isOrgTimezone } = useOrganizationData();
  const displayInfo = useMemo<{ label: string; text: string }>(() => {
    try {
      const activeTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const date = new Date();

      // Get short name (e.g., EST, PST)
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short',
        timeZone: activeTimezone,
      }).formatToParts(date);
      const shortName = parts.find((p) => p.type === 'timeZoneName')?.value || '';

      // Format: "America/New_York EST"
      // Clean up underscores for display
      const cleanTzName = activeTimezone.replace(/_/g, ' ');

      if (isOrgTimezone) {
        return {
          label: 'Organization Timezone:',
          text: `${cleanTzName} ${shortName}`,
        };
      }
      return {
        label: 'Timezone:',
        text: `${cleanTzName} ${shortName}`,
      };
    } catch (e) {
      console.error('Error formatting timezone display:', e);
      return { label: '', text: '' };
    }
  }, [timezone, isOrgTimezone]);

  if (!displayInfo.text) return null;

  return (
    <Box
      sx={{
        ml: 2,
        display: { xs: 'none', md: 'flex' }, // Hide on small screens if needed
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'center',
        color: 'text.primary',
        fontSize: '0.75rem',
        lineHeight: 1.2,
        textAlign: 'right',
        mr: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant='caption' sx={{ fontSize: '0.7rem', color: 'text.primary', fontWeight: 400 }}>
          {displayInfo.label}
        </Typography>
        {isOrgTimezone && (
          <Tooltip title='All users of this organization will see date/time in the same timezone'>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.primary', opacity: 0.6, cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
      <Typography variant='body2' sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
        {displayInfo.text}
      </Typography>
    </Box>
  );
};

const LanguageButton: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { i18n } = useTranslation();
  const openPopover = useField<boolean>(false);

  const handleOpenPopover = useCallback(() => {
    openPopover.setValue(true);
  }, [openPopover]);

  const handleClosePopover = useCallback(() => {
    openPopover.setValue(false);
  }, [openPopover]);

  return (
    <>
      <IconButton onClick={handleOpenPopover} ref={setAnchorEl} sx={{ ml: 1 }}>
        <Box
          sx={{
            display: 'flex',
            height: 20,
            width: 20,
            '& img': {
              width: '100%',
            },
          }}
        >
          <Image alt='' height={20} src={languages[i18n.language as LanguageKey] || languages.en} width={20} />
        </Box>
      </IconButton>
      <LanguagePopover anchorEl={anchorEl} onClose={handleClosePopover} open={openPopover.value} />
    </>
  );
};

const DarkmodeButton: React.FC = () => {
  const { appdata, setAppDataProperties } = useAppData();

  const handleDarkmode = useCallback(() => {
    const newTheme = appdata.theme === 'dark' ? 'light' : 'dark';
    setAppDataProperties({ theme: newTheme });
  }, [appdata.theme, setAppDataProperties]);

  return (
    <>
      <Tooltip title='Mode'>
        <IconButton onClick={handleDarkmode} sx={{ ml: 1 }}>
          <DarkMode fontSize='small' />
        </IconButton>
      </Tooltip>
    </>
  );
};

type AccountButtonProps = {
  user?: User | null;
};

const AccountButton: React.FC<AccountButtonProps> = ({ user }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const openPopover = useField<boolean>(false);
  const { keycloak, initialized } = useKeycloak();
  const loggingOut = useField<boolean>(false);

  const handleOpenPopover = useCallback(() => {
    openPopover.setValue(true);
  }, [openPopover]);

  const handleClosePopover = useCallback(() => {
    if (loggingOut.value) return;
    openPopover.setValue(false);
  }, [openPopover, loggingOut.value]);

  const handleLogout = useCallback(() => {
    if (loggingOut.value || !initialized || !keycloak || typeof (keycloak as any).logout !== 'function') return;

    loggingOut.setValue(true);
    // Defer logout until GTM fires the intercom_shutdown tag (or the
    // helper's 2.5s safety timeout, whichever first). The helper
    // guarantees the callback runs even when GTM is blocked. Keep the
    // popover open and show a 'Signing out…' affordance so the user
    // sees feedback during the (usually sub-second, max 2.5s) wait.
    // runSessionLogout latches the sign-out screen and clears
    // subject-scoped caches, ETags, and validated organizations before
    // the redirect, so a shared device cannot inherit this session's
    // state; on failure it unlatches, and we re-enable the menu entry.
    pushIntercomEvent('intercom_shutdown', {}, () => {
      void runSessionLogout(() => (keycloak as any).logout()).catch(() => {
        loggingOut.setValue(false);
        toast.error('Unable to sign out right now. Please try again.');
      });
    });
  }, [initialized, keycloak, loggingOut]);

  const currentOrganization = useCommittedOrganization();

  return (
    <>
      <IconButton
        component={ButtonBase}
        onClick={handleOpenPopover}
        ref={setAnchorEl}
        sx={{
          alignItems: 'center',
          display: 'flex',
          ml: 2,
        }}
      >
        <AccountCircleIcon color='inherit' fontSize='large' />
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClosePopover}
        open={openPopover.value}
        PaperProps={{ sx: { width: 300 } }}
        transitionDuration={0}
      >
        <Box
          sx={{
            alignItems: 'center',
            p: 2,
            display: 'flex',
          }}
        >
          <AccountCircleIcon color='inherit' sx={{ width: '3rem', height: '3rem' }} />
          <Box
            sx={{
              ml: 1,
            }}
          >
            {user ? (
              <>
                <Typography variant='body1'>
                  {user.first_name} {user.last_name}
                </Typography>
                <Typography color='textSecondary' variant='body2'>
                  {user.email}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant='body1'>Signed in</Typography>
                <Typography color='textSecondary' variant='body2'>
                  No organization access
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <Divider />
        <Box sx={{ my: 1 }}>
          {currentOrganization ? (
            <>
              <MenuItem component={NextLink} href={`/${currentOrganization}/account`} onClick={handleClosePopover}>
                <ListItemIcon>
                  <CogIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText primary={<Typography variant='body1'>Account Settings</Typography>} />
              </MenuItem>
              <Divider />
            </>
          ) : null}
          <MenuItem onClick={handleLogout} disabled={loggingOut.value}>
            <ListItemIcon>
              <LogoutIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText primary={<Typography variant='body1'>{loggingOut.value ? 'Signing out…' : 'Logout'}</Typography>} />
          </MenuItem>
        </Box>
      </Popover>
    </>
  );
};

const getDisabledStatusLanguage = (org: Organization, formatZonedDate: ReturnType<typeof useTimezoneFormatter>['format']): React.ReactElement => {
  const language = getOrganizationStatusMessage(org, formatZonedDate);
  return language ? <Alert severity='error'>{language}</Alert> : <></>;
};

export const MainNavbar: React.FC<MainNavbarProps> = ({ onOpenSidebar, user, ...other }) => {
  const { organization } = useOrganizationData();
  const { format: formatZonedDate } = useTimezoneFormatter();

  return (
    <>
      <MainNavbarRoot
        sx={{
          left: {
            lg: 280,
          },
          width: {
            lg: 'calc(100% - 280px)',
          },
        }}
        {...other}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: 64,
            left: 0,
            px: 2,
          }}
        >
          <IconButton
            onClick={onOpenSidebar}
            sx={{
              display: {
                xs: 'inline-flex',
                lg: 'none',
              },
            }}
          >
            <MenuIcon fontSize='small' />
          </IconButton>

          <Box>{organization ? getDisabledStatusLanguage(organization, formatZonedDate) : <></>}</Box>

          <Box sx={{ flexGrow: 1 }} />
          <TimezoneDisplay />
          <LanguageButton />
          <DarkmodeButton />
          <AccountButton user={user} />
        </Toolbar>
      </MainNavbarRoot>
    </>
  );
};
